import { useState, useEffect } from "react";
import { useAuth } from "../hooks/useAuth";
import { useLanguage } from "../contexts/LanguageContext";
import Sidebar from "../components/Sidebar";
import { api } from "../api";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  AreaChart,
  Area,
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ZAxis,
} from "recharts";

// Activity Monitor-inspired process data structure
interface AIProcess {
  id: string;
  name: string;
  cpu: number; // Quantum Coherence/Processing
  memory: number; // Context Dimensions/State Storage
  disk: number; // Temporal States/Cache Usage
  network: number; // Agent Communication/Entanglement
  status: "running" | "sleeping" | "stopped";
  pid: string;
  user: string;
}

// SVG Icon Components
const BrainIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
  </svg>
);

const QuantumIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
  </svg>
);

const TimeIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const DimensionIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
  </svg>
);

const EvolutionIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
  </svg>
);

const PredictionIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
  </svg>
);

interface RevolutionaryMetrics {
  predictive: {
    totalUsers: number;
    totalPreloads: number;
    averageAccuracy: number;
    quantumStates: number;
  };
  evolution: {
    totalAgents: number;
    currentGeneration: number;
    averageFitness: number;
    totalMutations: number;
  };
  quantum: {
    totalAgents: number;
    coherenceLevel: number;
    entanglementDegree: number;
    averageQuantumSpeedup: number;
  };
  temporal: {
    totalStates: number;
    totalBranches: number;
    averageStatesPerBranch: number;
  };
  preExecution: {
    totalPreExecutions: number;
    cacheHitRate: number;
    predictionAccuracy: number;
  };
  hyperDimensional: {
    totalContexts: number;
    averageDimensions: number;
    foldedContexts: number;
  };
  overall: {
    totalInnovations: number;
    systemHealth: number;
    revolutionScore: number;
  };
}

export default function RevolutionaryDashboard() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [metrics, setMetrics] = useState<RevolutionaryMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedTab, setSelectedTab] = useState<"overview" | "processes" | "predictive" | "quantum" | "temporal" | "evolution" | "hyperdimensional">("overview");
  const [processes, setProcesses] = useState<AIProcess[]>([]);
  const [resourceHistory, setResourceHistory] = useState<{
    cpu: number[];
    memory: number[];
    disk: number[];
    network: number[];
  }>({ cpu: [], memory: [], disk: [], network: [] });
  const [sortBy, setSortBy] = useState<"cpu" | "memory" | "disk" | "network">("cpu");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");

  // Fetch metrics from API
  useEffect(() => {
    const fetchMetrics = async () => {
      setLoading(true);
      
      try {
        const response = await api.getRevolutionaryMetrics();
        const data = response.data || response;
        setMetrics(data);
        
        // Generate AI processes from metrics (Activity Monitor style)
        const aiProcesses: AIProcess[] = [
          {
            id: "predictive-orchestrator",
            name: "Predictive Orchestrator",
            cpu: metrics?.predictive.averageAccuracy * 100 || 0,
            memory: metrics?.predictive.quantumStates / 10 || 0,
            disk: 0,
            network: metrics?.predictive.totalPreloads / 10 || 0,
            status: "running",
            pid: "1001",
            user: "system"
          },
          {
            id: "autonomous-evolution",
            name: "Autonomous Evolution",
            cpu: metrics?.evolution.averageFitness * 100 || 0,
            memory: metrics?.evolution.totalAgents * 5 || 0,
            disk: metrics?.evolution.totalMutations / 10 || 0,
            network: 0,
            status: "running",
            pid: "1002",
            user: "system"
          },
          {
            id: "quantum-swarm",
            name: "Quantum Swarm",
            cpu: metrics?.quantum.coherenceLevel * 100 || 0,
            memory: metrics?.quantum.totalAgents * 10 || 0,
            disk: 0,
            network: metrics?.quantum.entanglementDegree * 100 || 0,
            status: "running",
            pid: "1003",
            user: "system"
          },
          {
            id: "temporal-cache",
            name: "Temporal Cache",
            cpu: 15,
            memory: metrics?.temporal.totalStates / 20 || 0,
            disk: metrics?.temporal.totalStates / 5 || 0,
            network: 0,
            status: "running",
            pid: "1004",
            user: "system"
          },
          {
            id: "zero-latency-preexec",
            name: "Zero-Latency Pre-Execution",
            cpu: metrics?.preExecution.predictionAccuracy * 100 || 0,
            memory: metrics?.preExecution.totalPreExecutions / 5 || 0,
            disk: metrics?.preExecution.cacheHitRate * 100 || 0,
            network: 0,
            status: "running",
            pid: "1005",
            user: "system"
          },
          {
            id: "hyper-dimensional-context",
            name: "Hyper-Dimensional Context",
            cpu: 25,
            memory: metrics?.hyperDimensional.averageDimensions / 2 || 0,
            disk: metrics?.hyperDimensional.foldedContexts * 5 || 0,
            network: 0,
            status: "running",
            pid: "1006",
            user: "system"
          },
          {
            id: "universal-translation",
            name: "Universal Translation",
            cpu: 20,
            memory: 50,
            disk: 0,
            network: 30,
            status: "sleeping",
            pid: "1007",
            user: "system"
          }
        ];
        
        setProcesses(aiProcesses);
        
        // Update resource history for graphs
        setResourceHistory(prev => ({
          cpu: [...prev.cpu.slice(-29), aiProcesses.reduce((sum, p) => sum + p.cpu, 0) / aiProcesses.length],
          memory: [...prev.memory.slice(-29), aiProcesses.reduce((sum, p) => sum + p.memory, 0) / aiProcesses.length],
          disk: [...prev.disk.slice(-29), aiProcesses.reduce((sum, p) => sum + p.disk, 0) / aiProcesses.length],
          network: [...prev.network.slice(-29), aiProcesses.reduce((sum, p) => sum + p.network, 0) / aiProcesses.length]
        }));
      } catch (error) {
        console.error('Error fetching revolutionary metrics:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchMetrics();
    const interval = setInterval(fetchMetrics, 2000); // Faster updates like Activity Monitor
    return () => clearInterval(interval);
  }, []);

  // Sort processes
  const sortedProcesses = [...processes].sort((a, b) => {
    const modifier = sortOrder === "asc" ? 1 : -1;
    return (a[sortBy] - b[sortBy]) * modifier;
  });

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-500">Loading revolutionary dashboard...</p>
        </div>
      </div>
    );
  }

  if (!metrics) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center text-red-600">
          <p>Failed to load metrics</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-slate-50 to-purple-50">
      <Sidebar />
      <div className="flex-1">
        {/* Header */}
        <header className="bg-white/80 backdrop-blur-sm border-b border-gray-200 px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Revolutionary Dashboard</h1>
              <p className="text-sm text-gray-500">Beyond Industry Standards Agentic Platform</p>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right">
                <div className="text-3xl font-bold text-purple-600">
                  {(metrics.overall.revolutionScore * 100).toFixed(0)}%
                </div>
                <div className="text-sm text-gray-500">Revolution Score</div>
              </div>
            </div>
          </div>
        </header>

        {/* Tabs */}
        <div className="bg-white border-b border-gray-200 px-6">
          <div className="flex gap-1">
            {[
              { id: "overview", label: "Overview", icon: <BrainIcon /> },
              { id: "processes", label: "Processes", icon: <QuantumIcon /> },
              { id: "predictive", label: "Predictive", icon: <PredictionIcon /> },
              { id: "quantum", label: "Quantum", icon: <QuantumIcon /> },
              { id: "temporal", label: "Temporal", icon: <TimeIcon /> },
              { id: "evolution", label: "Evolution", icon: <EvolutionIcon /> },
              { id: "hyperdimensional", label: "Hyper-Dimensional", icon: <DimensionIcon /> },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setSelectedTab(tab.id as any)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors ${
                  selectedTab === tab.id
                    ? "text-purple-600 border-b-2 border-purple-600 bg-purple-50"
                    : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                }`}
              >
                {tab.icon}
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {selectedTab === "overview" && <OverviewTab metrics={metrics} resourceHistory={resourceHistory} />}
          {selectedTab === "processes" && <ProcessesTab processes={sortedProcesses} sortBy={sortBy} sortOrder={sortOrder} onSortBy={(field) => setSortBy(field)} onSortOrder={() => setSortOrder(sortOrder === "asc" ? "desc" : "asc")} />}
          {selectedTab === "predictive" && <PredictiveTab metrics={metrics} />}
          {selectedTab === "quantum" && <QuantumTab metrics={metrics} />}
          {selectedTab === "temporal" && <TemporalTab metrics={metrics} />}
          {selectedTab === "evolution" && <EvolutionTab metrics={metrics} />}
          {selectedTab === "hyperdimensional" && <HyperDimensionalTab metrics={metrics} />}
        </div>
      </div>
    </div>
  );
}

function OverviewTab({ metrics, resourceHistory }: { metrics: RevolutionaryMetrics; resourceHistory: { cpu: number[]; memory: number[]; disk: number[]; network: number[] } }) {
  const resourceData = resourceHistory.cpu.map((_, i) => ({
    time: i,
    cpu: resourceHistory.cpu[i],
    memory: resourceHistory.memory[i],
    disk: resourceHistory.disk[i],
    network: resourceHistory.network[i]
  }));

  return (
    <div className="space-y-6">
      {/* Resource Monitoring - Activity Monitor Style */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <ResourceCard
          title="Quantum Processing"
          value={resourceHistory.cpu[resourceHistory.cpu.length - 1]?.toFixed(1) || "0"}
          unit="%"
          color="red"
          icon={<QuantumIcon />}
        />
        <ResourceCard
          title="Context Storage"
          value={resourceHistory.memory[resourceHistory.memory.length - 1]?.toFixed(1) || "0"}
          unit="%"
          color="blue"
          icon={<DimensionIcon />}
        />
        <ResourceCard
          title="Temporal Cache"
          value={resourceHistory.disk[resourceHistory.disk.length - 1]?.toFixed(1) || "0"}
          unit="%"
          color="purple"
          icon={<TimeIcon />}
        />
        <ResourceCard
          title="Agent Network"
          value={resourceHistory.network[resourceHistory.network.length - 1]?.toFixed(1) || "0"}
          unit="%"
          color="green"
          icon={<EvolutionIcon />}
        />
      </div>

      {/* Resource History Graphs */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
          <h3 className="font-semibold text-gray-900 mb-2 text-sm">Quantum Processing</h3>
          <ResponsiveContainer width="100%" height={120}>
            <AreaChart data={resourceData}>
              <defs>
                <linearGradient id="colorCpu" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#ef4444" stopOpacity={0.8}/>
                  <stop offset="95%" stopColor="#ef4444" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis hide />
              <YAxis hide />
              <Tooltip />
              <Area type="monotone" dataKey="cpu" stroke="#ef4444" fill="url(#colorCpu" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
          <h3 className="font-semibold text-gray-900 mb-2 text-sm">Context Storage</h3>
          <ResponsiveContainer width="100%" height={120}>
            <AreaChart data={resourceData}>
              <defs>
                <linearGradient id="colorMemory" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.8}/>
                  <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis hide />
              <YAxis hide />
              <Tooltip />
              <Area type="monotone" dataKey="memory" stroke="#3b82f6" fill="url(#colorMemory" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          title="Total Innovations"
          value={metrics.overall.totalInnovations.toLocaleString()}
          icon={<BrainIcon />}
          color="purple"
        />
        <MetricCard
          title="System Health"
          value={`${(metrics.overall.systemHealth * 100).toFixed(0)}%`}
          icon={<QuantumIcon />}
          color="blue"
        />
        <MetricCard
          title="Revolution Score"
          value={`${(metrics.overall.revolutionScore * 100).toFixed(0)}%`}
          icon={<EvolutionIcon />}
          color="green"
        />
        <MetricCard
          title="Active Agents"
          value={metrics.evolution.totalAgents}
          icon={<PredictionIcon />}
          color="orange"
        />
      </div>

      {/* Innovation Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
          <h3 className="font-semibold text-gray-900 mb-4">Innovation Distribution</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={[
              { name: "Predictive", value: metrics.predictive.totalPreloads },
              { name: "Evolution", value: metrics.evolution.totalMutations },
              { name: "Quantum", value: metrics.quantum.totalAgents },
              { name: "Temporal", value: metrics.temporal.totalStates },
              { name: "Pre-Exec", value: metrics.preExecution.totalPreExecutions },
              { name: "Hyper-D", value: metrics.hyperDimensional.totalContexts },
            ]}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="value" fill="#8b5cf6" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
          <h3 className="font-semibold text-gray-900 mb-4">Performance Metrics</h3>
          <div className="space-y-4">
            <MetricRow
              label="Prediction Accuracy"
              value={`${(metrics.predictive.averageAccuracy * 100).toFixed(0)}%`}
              color="purple"
            />
            <MetricRow
              label="Quantum Speedup"
              value={`${metrics.quantum.averageQuantumSpeedup.toFixed(1)}x`}
              color="blue"
            />
            <MetricRow
              label="Agent Fitness"
              value={`${(metrics.evolution.averageFitness * 100).toFixed(0)}%`}
              color="green"
            />
            <MetricRow
              label="Cache Hit Rate"
              value={`${(metrics.preExecution.cacheHitRate * 100).toFixed(0)}%`}
              color="orange"
            />
            <MetricRow
              label="Quantum Coherence"
              value={`${(metrics.quantum.coherenceLevel * 100).toFixed(0)}%`}
              color="purple"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function PredictiveTab({ metrics }: { metrics: RevolutionaryMetrics }) {
  const predictionData = [
    { time: "00:00", accuracy: 0.75, preloads: 10 },
    { time: "04:00", accuracy: 0.78, preloads: 15 },
    { time: "08:00", accuracy: 0.85, preloads: 35 },
    { time: "12:00", accuracy: 0.92, preloads: 45 },
    { time: "16:00", accuracy: 0.88, preloads: 38 },
    { time: "20:00", accuracy: 0.82, preloads: 25 },
  ];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <MetricCard
          title="Total Users"
          value={metrics.predictive.totalUsers}
          icon={<PredictionIcon />}
          color="purple"
        />
        <MetricCard
          title="Active Preloads"
          value={metrics.predictive.totalPreloads}
          icon={<PredictionIcon />}
          color="blue"
        />
        <MetricCard
          title="Avg Accuracy"
          value={`${(metrics.predictive.averageAccuracy * 100).toFixed(0)}%`}
          icon={<PredictionIcon />}
          color="green"
        />
      </div>

      <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
        <h3 className="font-semibold text-gray-900 mb-4">Prediction Accuracy Over Time</h3>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={predictionData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="time" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Line type="monotone" dataKey="accuracy" stroke="#8b5cf6" strokeWidth={2} name="Accuracy" />
            <Line type="monotone" dataKey="preloads" stroke="#3b82f6" strokeWidth={2} name="Preloads" />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function QuantumTab({ metrics }: { metrics: RevolutionaryMetrics }) {
  const quantumData = Array.from({ length: 20 }, (_, i) => ({
    x: Math.random() * 100,
    y: Math.random() * 100,
    z: Math.random() * 100,
    coherence: metrics.quantum.coherenceLevel + (Math.random() - 0.5) * 0.1,
  }));

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <MetricCard
          title="Quantum Agents"
          value={metrics.quantum.totalAgents}
          icon={<QuantumIcon />}
          color="purple"
        />
        <MetricCard
          title="Coherence Level"
          value={`${(metrics.quantum.coherenceLevel * 100).toFixed(0)}%`}
          icon={<QuantumIcon />}
          color="blue"
        />
        <MetricCard
          title="Entanglement"
          value={`${(metrics.quantum.entanglementDegree * 100).toFixed(0)}%`}
          icon={<QuantumIcon />}
          color="green"
        />
        <MetricCard
          title="Avg Speedup"
          value={`${metrics.quantum.averageQuantumSpeedup.toFixed(1)}x`}
          icon={<QuantumIcon />}
          color="orange"
        />
      </div>

      <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
        <h3 className="font-semibold text-gray-900 mb-4">Quantum State Space</h3>
        <ResponsiveContainer width="100%" height={400}>
          <ScatterChart>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="x" name="Qubit 1" />
            <YAxis dataKey="y" name="Qubit 2" />
            <ZAxis dataKey="z" range={[50, 400]} name="Coherence" />
            <Tooltip cursor={{ strokeDasharray: "3 3" }} />
            <Scatter data={quantumData} fill="#8b5cf6" />
          </ScatterChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function TemporalTab({ metrics }: { metrics: RevolutionaryMetrics }) {
  const timelineData = Array.from({ length: 12 }, (_, i) => ({
    branch: `Branch ${i + 1}`,
    states: Math.floor(Math.random() * 200) + 50,
    complexity: Math.random(),
  }));

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <MetricCard
          title="Total States"
          value={metrics.temporal.totalStates}
          icon={<TimeIcon />}
          color="purple"
        />
        <MetricCard
          title="Timeline Branches"
          value={metrics.temporal.totalBranches}
          icon={<TimeIcon />}
          color="blue"
        />
        <MetricCard
          title="Avg States/Branch"
          value={Math.round(metrics.temporal.averageStatesPerBranch)}
          icon={<TimeIcon />}
          color="green"
        />
      </div>

      <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
        <h3 className="font-semibold text-gray-900 mb-4">Timeline Branch Distribution</h3>
        <ResponsiveContainer width="100%" height={300}>
          <AreaChart data={timelineData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="branch" />
            <YAxis />
            <Tooltip />
            <Area type="monotone" dataKey="states" stroke="#8b5cf6" fill="#8b5cf6" fillOpacity={0.3} />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function EvolutionTab({ metrics }: { metrics: RevolutionaryMetrics }) {
  const evolutionData = Array.from({ length: metrics.evolution.currentGeneration }, (_, i) => ({
    generation: i + 1,
    fitness: 0.5 + Math.random() * 0.4,
    mutations: Math.floor(Math.random() * 20) + 5,
  }));

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <MetricCard
          title="Total Agents"
          value={metrics.evolution.totalAgents}
          icon={<EvolutionIcon />}
          color="purple"
        />
        <MetricCard
          title="Current Generation"
          value={metrics.evolution.currentGeneration}
          icon={<EvolutionIcon />}
          color="blue"
        />
        <MetricCard
          title="Avg Fitness"
          value={`${(metrics.evolution.averageFitness * 100).toFixed(0)}%`}
          icon={<EvolutionIcon />}
          color="green"
        />
        <MetricCard
          title="Total Mutations"
          value={metrics.evolution.totalMutations}
          icon={<EvolutionIcon />}
          color="orange"
        />
      </div>

      <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
        <h3 className="font-semibold text-gray-900 mb-4">Evolution Progress</h3>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={evolutionData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="generation" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Line type="monotone" dataKey="fitness" stroke="#8b5cf6" strokeWidth={2} name="Fitness" />
            <Line type="monotone" dataKey="mutations" stroke="#3b82f6" strokeWidth={2} name="Mutations" />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function HyperDimensionalTab({ metrics }: { metrics: RevolutionaryMetrics }) {
  const contextData = Array.from({ length: 50 }, (_, i) => ({
    dimension: i + 1,
    weight: Math.random(),
    similarity: Math.random(),
  }));

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <MetricCard
          title="Total Contexts"
          value={metrics.hyperDimensional.totalContexts}
          icon={<DimensionIcon />}
          color="purple"
        />
        <MetricCard
          title="Avg Dimensions"
          value={metrics.hyperDimensional.averageDimensions}
          icon={<DimensionIcon />}
          color="blue"
        />
        <MetricCard
          title="Folded Contexts"
          value={metrics.hyperDimensional.foldedContexts}
          icon={<DimensionIcon />}
          color="green"
        />
      </div>

      <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
        <h3 className="font-semibold text-gray-900 mb-4">Dimensional Weight Distribution</h3>
        <ResponsiveContainer width="100%" height={300}>
          <AreaChart data={contextData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="dimension" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Area type="monotone" dataKey="weight" stroke="#8b5cf6" fill="#8b5cf6" fillOpacity={0.3} name="Weight" />
            <Area type="monotone" dataKey="similarity" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.3} name="Similarity" />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function MetricCard({ title, value, icon, color }: { title: string; value: string | number; icon: React.ReactNode; color: string }) {
  const colorClasses = {
    purple: "text-purple-600 bg-purple-50",
    blue: "text-blue-600 bg-blue-50",
    green: "text-green-600 bg-green-50",
    orange: "text-orange-600 bg-orange-50",
  };

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow">
      <div className="flex items-center justify-between mb-2">
        <span className="text-gray-600 text-sm font-medium">{title}</span>
        <span className={colorClasses[color as keyof typeof colorClasses]}>
          {icon}
        </span>
      </div>
      <div className="text-3xl font-bold text-gray-900">{value}</div>
    </div>
  );
}

function ProcessesTab({ processes, sortBy, sortOrder, onSortBy, onSortOrder }: { processes: AIProcess[]; sortBy: "cpu" | "memory" | "disk" | "network"; sortOrder: "asc" | "desc"; onSortBy: (field: "cpu" | "memory" | "disk" | "network") => void; onSortOrder: () => void }) {
  return (
    <div className="space-y-4">
      {/* Process Table - Activity Monitor Style */}
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-4 py-2 text-left font-medium text-gray-600">PID</th>
              <th className="px-4 py-2 text-left font-medium text-gray-600">Process Name</th>
              <th className="px-4 py-2 text-left font-medium text-gray-600 cursor-pointer hover:bg-gray-100" onClick={() => onSortBy("cpu")}>Quantum % {sortBy === "cpu" && (sortOrder === "asc" ? "↑" : "↓")}</th>
              <th className="px-4 py-2 text-left font-medium text-gray-600 cursor-pointer hover:bg-gray-100" onClick={() => onSortBy("memory")}>Context % {sortBy === "memory" && (sortOrder === "asc" ? "↑" : "↓")}</th>
              <th className="px-4 py-2 text-left font-medium text-gray-600 cursor-pointer hover:bg-gray-100" onClick={() => onSortBy("disk")}>Temporal % {sortBy === "disk" && (sortOrder === "asc" ? "↑" : "↓")}</th>
              <th className="px-4 py-2 text-left font-medium text-gray-600 cursor-pointer hover:bg-gray-100" onClick={() => onSortBy("network")}>Network % {sortBy === "network" && (sortOrder === "asc" ? "↑" : "↓")}</th>
              <th className="px-4 py-2 text-left font-medium text-gray-600">User</th>
              <th className="px-4 py-2 text-left font-medium text-gray-600">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {processes.map((process) => (
              <tr key={process.id} className="hover:bg-gray-50">
                <td className="px-4 py-2 text-gray-600 font-mono text-xs">{process.pid}</td>
                <td className="px-4 py-2 text-gray-900 font-medium">{process.name}</td>
                <td className="px-4 py-2">
                  <div className="flex items-center gap-2">
                    <div className="w-20 h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div className="h-full bg-red-500 rounded-full" style={{ width: `${process.cpu}%` }}></div>
                    </div>
                    <span className="text-gray-600 w-12">{process.cpu.toFixed(1)}%</span>
                  </div>
                </td>
                <td className="px-4 py-2">
                  <div className="flex items-center gap-2">
                    <div className="w-20 h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div className="h-full bg-blue-500 rounded-full" style={{ width: `${process.memory}%` }}></div>
                    </div>
                    <span className="text-gray-600 w-12">{process.memory.toFixed(1)}%</span>
                  </div>
                </td>
                <td className="px-4 py-2">
                  <div className="flex items-center gap-2">
                    <div className="w-20 h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div className="h-full bg-purple-500 rounded-full" style={{ width: `${process.disk}%` }}></div>
                    </div>
                    <span className="text-gray-600 w-12">{process.disk.toFixed(1)}%</span>
                  </div>
                </td>
                <td className="px-4 py-2">
                  <div className="flex items-center gap-2">
                    <div className="w-20 h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div className="h-full bg-green-500 rounded-full" style={{ width: `${process.network}%` }}></div>
                    </div>
                    <span className="text-gray-600 w-12">{process.network.toFixed(1)}%</span>
                  </div>
                </td>
                <td className="px-4 py-2 text-gray-600">{process.user}</td>
                <td className="px-4 py-2">
                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                    process.status === "running" ? "bg-green-100 text-green-700" :
                    process.status === "sleeping" ? "bg-yellow-100 text-yellow-700" :
                    "bg-red-100 text-red-700"
                  }`}>
                    {process.status}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function MetricRow({ label, value, color }: { label: string; value: string; color: string }) {
  const colorClasses = {
    purple: "text-purple-600",
    blue: "text-blue-600",
    green: "text-green-600",
    orange: "text-orange-600",
  };

  return (
    <div className="flex items-center justify-between">
      <span className="text-gray-600">{label}</span>
      <span className={`font-semibold ${colorClasses[color as keyof typeof colorClasses]}`}>{value}</span>
    </div>
  );
}

function ResourceCard({ title, value, unit, color, icon }: { title: string; value: string; unit: string; color: string; icon: React.ReactNode }) {
  const colorClasses = {
    red: "text-red-600 bg-red-50 border-red-200",
    blue: "text-blue-600 bg-blue-50 border-blue-200",
    purple: "text-purple-600 bg-purple-50 border-purple-200",
    green: "text-green-600 bg-green-50 border-green-200",
  };

  return (
    <div className={`bg-white border border-gray-200 rounded-xl p-4 shadow-sm ${colorClasses[color as keyof typeof colorClasses].split(' ')[2]}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-gray-600 text-xs font-medium uppercase tracking-wide">{title}</span>
        <span className={colorClasses[color as keyof typeof colorClasses].split(' ')[0]}>
          {icon}
        </span>
      </div>
      <div className="text-2xl font-bold text-gray-900">{value}<span className="text-sm font-normal text-gray-500 ml-1">{unit}</span></div>
    </div>
  );
}
